import type { LeagueSummary, Schemas } from "@softmax/observatory-client/types";
import {
  curatedCoworldKeys,
  curationOn,
  normalizeCoworldKey,
} from "@softmax/web-features/coworlds";
import { watchableLeagues } from "@softmax/web-features/leagues";
import { featuredCoworlds } from "@softmax/web-features/site/records";
import { publicRead } from "./api";

export function listedGamePage({
  cursor,
  signal,
}: {
  cursor: string | null;
  signal?: AbortSignal | undefined;
}) {
  const params = new URLSearchParams({ limit: "12" });

  for (const name of featuredCoworlds)
    params.append("preferred_coworld_key", normalizeCoworldKey(name));

  if (curationOn())
    for (const key of curatedCoworldKeys)
      params.append("game_of_week_coworld_key", key);

  if (cursor !== null) params.set("cursor", cursor);

  return publicRead<Schemas["ListedGameCatalogPage"]>({
    path: `/v2/leagues/catalog/games?${params}`,
    signal,
  });
}

export async function navigationLeagues({
  signal,
}: {
  signal?: AbortSignal | undefined;
} = {}): Promise<LeagueSummary[]> {
  const params = new URLSearchParams({ limit: "12" });

  for (const name of featuredCoworlds)
    params.append("preferred_coworld_key", normalizeCoworldKey(name));

  if (curationOn())
    for (const key of curatedCoworldKeys) {
      params.append("coworld_key", key);
      params.append("game_of_week_coworld_key", key);
    }

  const page = await publicRead<Schemas["ListedGameCatalogPage"]>({
    path: `/v2/leagues/catalog/games?${params}`,
    signal,
  });

  if (curationOn() && page.next_cursor !== null)
    throw new Error("Navigation catalog exceeded its selected games");

  return watchableLeagues(page.leagues);
}

async function publicLeagues({
  signal,
}: {
  signal?: AbortSignal | undefined;
}): Promise<LeagueSummary[]> {
  const leagues: LeagueSummary[] = [];
  let cursor: string | null = null;

  do {
    const params = new URLSearchParams({ limit: "50" });

    if (cursor !== null) params.set("cursor", cursor);

    let page = await publicRead<Schemas["LeagueCatalogPage"]>({
      path: `/v2/leagues/catalog?${params}`,
      signal,
    });

    if (page.oversized) {
      params.set("limit", "1");
      params.set("cacheable", "false");
      page = await publicRead<Schemas["LeagueCatalogPage"]>({
        path: `/v2/leagues/catalog?${params}`,
        signal,
      });
    }

    leagues.push(...page.leagues);
    cursor = page.next_cursor;
  } while (cursor !== null);

  return leagues;
}

export function watchLeagues({
  signal,
}: {
  signal?: AbortSignal | undefined;
} = {}): Promise<LeagueSummary[] | null> {
  return publicLeagues({ signal }).then(watchableLeagues, () => {
    signal?.throwIfAborted();
    return null;
  });
}
